import { FormSwitch, Select } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import type { IncomingInvoiceApprovalHandoverSchema } from "@msuite/utils/schemas";
import React from "react";
import { useFormContext } from "react-hook-form";
import { guardString } from "@msuite/utils/strings";
import { useIncomingInvoiceHandoverCandidates } from "@/hooks/use-incoming-invoice-handover-candidates";

/** Props Interface */
interface IncomingInvoiceApprovalHandoverFormProps {
	/** The invoice to hand over; its candidates leave out the people already asked. */
	incomingInvoiceId: string;
	/** Only a default approver hands the invoice over as its sole approval. */
	canHandOverSole: boolean;
}

export function IncomingInvoiceApprovalHandoverForm({
	incomingInvoiceId,
	canHandOverSole,
}: IncomingInvoiceApprovalHandoverFormProps) {
	/** Context */
	const methods = useFormContext<IncomingInvoiceApprovalHandoverSchema>();

	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	// Only members who may approve invoices at all, and nobody already asked: the server refuses both.
	const { candidates } = useIncomingInvoiceHandoverCandidates(incomingInvoiceId);

	/** Computed */
	const options = candidates.map((candidate) => ({
		value: candidate.id,
		label: guardString(candidate.name),
		item: null,
	}));

	/** Render */
	return (
		<React.Fragment>
			<Select
				control={methods.control}
				name="userId"
				label={t("incoming_invoices.approver")}
				placeholder={t("placeholders.select_member")}
				isRequired
				isNullable={false}
				options={options}
				helperText={t("incoming_invoices.approvers_need_read_rule")}
				data-cy="incoming-invoice-handover-user-select"
			/>
			{canHandOverSole && (
				<FormSwitch
					control={methods.control}
					name="isSoleApprover"
					data-cy="incoming-invoice-handover-sole-switch"
				>
					<div className="v-stack gap-0.5">
						<span>{t("incoming_invoices.handover.sole_label")}</span>
						<span className="label">{t("incoming_invoices.handover.sole_description")}</span>
					</div>
				</FormSwitch>
			)}
		</React.Fragment>
	);
}
